import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUSES, FINISHES, fmtDate, fmtDateTime } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Image as ImageIcon, Loader2, ListChecks, Pencil, Trash2, X } from "lucide-react";

export default function Jobs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canBulk = user?.role !== "operator";
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [finish, setFinish] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [staffList, setStaffList] = useState([]);
  const [bulkStatus, setBulkStatus] = useState({ open: false, status: "", remarks: "", busy: false });
  const [bulkEdit, setBulkEdit] = useState({ open: false, assigned: "keep", date: "", busy: false });
  const [bulkDelete, setBulkDelete] = useState({ open: false, busy: false });

  useEffect(() => {
    if (isAdmin) api.get("/staff").then((r) => setStaffList(r.data.filter((s) => s.active))).catch((e) => console.error("Failed to load staff:", e));
  }, [isAdmin]);

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });
  const allSelected = jobs.length > 0 && jobs.every((j) => selected.has(j.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(jobs.map((j) => j.id)));

  const applyBulkStatus = async () => {
    if (!bulkStatus.status) return toast.error("Select a status");
    setBulkStatus((b) => ({ ...b, busy: true }));
    try {
      const res = await api.post("/jobs/bulk-status", { job_ids: [...selected], new_status: bulkStatus.status, remarks: bulkStatus.remarks });
      toast.success(`Status updated for ${res.data.updated} job(s)${res.data.skipped ? ` · ${res.data.skipped} already ${bulkStatus.status}` : ""}${res.data.failed?.length ? ` · ${res.data.failed.length} skipped (no permission)` : ""}`);
      setBulkStatus({ open: false, status: "", remarks: "", busy: false });
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk status update failed"); setBulkStatus((b) => ({ ...b, busy: false })); }
  };

  const applyBulkEdit = async () => {
    const payload = { job_ids: [...selected] };
    if (isAdmin && bulkEdit.assigned !== "keep") payload.assigned_staff_id = bulkEdit.assigned === "none" ? null : bulkEdit.assigned;
    if (bulkEdit.date) payload.expected_completion_date = bulkEdit.date;
    if (!("assigned_staff_id" in payload) && !payload.expected_completion_date) return toast.error("Choose at least one field to edit");
    setBulkEdit((b) => ({ ...b, busy: true }));
    try {
      const res = await api.post("/jobs/bulk-update", payload);
      toast.success(`Updated ${res.data.updated} job(s)${res.data.failed?.length ? ` · ${res.data.failed.length} skipped` : ""}`);
      setBulkEdit({ open: false, assigned: "keep", date: "", busy: false });
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk edit failed"); setBulkEdit((b) => ({ ...b, busy: false })); }
  };

  const applyBulkDelete = async () => {
    setBulkDelete((b) => ({ ...b, busy: true }));
    try {
      const res = await api.post("/jobs/bulk-delete", { job_ids: [...selected] });
      toast.success(`Deleted ${res.data.deleted} job(s)`);
      setBulkDelete({ open: false, busy: false });
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); setBulkDelete({ open: false, busy: false }); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status !== "all") params.set("status", status);
      if (finish !== "all") params.set("finish", finish);
      const res = await api.get(`/jobs?${params.toString()}`);
      setJobs(res.data);
    } catch (e) { console.error("Failed to load jobs:", e); } finally { setLoading(false); }
  }, [q, status, finish]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="jobs-page">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Jobs</h1>
      <div className="flex flex-col sm:flex-row gap-3 mt-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="jobs-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Job no, customer, mobile, article…" className="pl-9 h-11" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="jobs-status-filter" className="sm:w-44 h-11"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="Pending" data-testid="status-filter-pending">Pending (New + Processing)</SelectItem>
            <SelectItem value="Ready">Ready</SelectItem>
            <SelectItem value="Delivered">Delivered</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={finish} onValueChange={setFinish}>
          <SelectTrigger data-testid="jobs-finish-filter" className="sm:w-44 h-11"><SelectValue placeholder="Finish" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Finishes</SelectItem>
            {FINISHES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk actions bar */}
      {canBulk && selected.size > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-accent/60 px-4 py-2.5" data-testid="bulk-actions-bar">
          <ListChecks className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} selected</span>
          <div className="flex-1" />
          <Button size="sm" variant="secondary" onClick={() => setBulkStatus({ open: true, status: "", remarks: "", busy: false })} data-testid="bulk-status-button">Change Status</Button>
          <Button size="sm" variant="secondary" onClick={() => setBulkEdit({ open: true, assigned: "keep", date: "", busy: false })} data-testid="bulk-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
          {isAdmin && (
            <Button size="sm" variant="destructive" onClick={() => setBulkDelete({ open: true, busy: false })} data-testid="bulk-delete-button"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear-button"><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>
        </div>
      )}

      {/* Desktop table */}
      <Card className="mt-4 hidden md:block">
        <Table data-testid="jobs-table">
          <TableHeader>
            <TableRow>
              {canBulk && (
                <TableHead className="w-10">
                  <Checkbox data-testid="job-select-all-checkbox" checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Select all jobs" />
                </TableHead>
              )}
              <TableHead>Job No</TableHead><TableHead>Customer</TableHead><TableHead>Article</TableHead>
              <TableHead>Finish</TableHead><TableHead>Date</TableHead><TableHead>Photos</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((j) => (
              <TableRow key={j.id} className={`cursor-pointer ${selected.has(j.id) ? "bg-accent/50" : ""}`} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-row-${j.id}`}>
                {canBulk && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox data-testid={`job-select-desktop-${j.id}`} checked={selected.has(j.id)} onCheckedChange={() => toggleSelect(j.id)} aria-label={`Select ${j.job_number}`} />
                  </TableCell>
                )}
                <TableCell className="font-semibold">{j.job_number}</TableCell>
                <TableCell><div>{j.customer_name}</div><div className="text-xs text-muted-foreground">{j.customer_mobile}</div></TableCell>
                <TableCell>{j.article_name}</TableCell>
                <TableCell className="text-xs">{(j.required_finishes || []).join(", ")}</TableCell>
                <TableCell className="text-xs">{fmtDateTime(j.created_at)}</TableCell>
                <TableCell><span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><ImageIcon className="h-3 w-3" />{j.photo_count}</span></TableCell>
                <TableCell><StatusBadge status={j.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && jobs.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground" data-testid="jobs-empty">No jobs found.</p>}
      </Card>

      {/* Mobile cards */}
      <div className="md:hidden mt-4 space-y-2" data-testid="jobs-cards">
        {jobs.map((j) => (
          <Card key={j.id} className={`active:scale-[0.99] transition-transform ${selected.has(j.id) ? "border-primary/60 bg-accent/40" : ""}`} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-card-${j.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {canBulk && (
                  <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                    <Checkbox data-testid={`job-select-mobile-${j.id}`} checked={selected.has(j.id)} onCheckedChange={() => toggleSelect(j.id)} aria-label={`Select ${j.job_number}`} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{j.job_number}</p>
                  <p className="text-sm truncate">{j.article_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{j.customer_name} · {j.customer_mobile}</p>
                  <p className="text-xs text-muted-foreground mt-1 truncate">{(j.required_finishes || []).join(", ")}</p>
                </div>
                <StatusBadge status={j.status} />
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">{fmtDateTime(j.created_at)} · {j.photo_count} photos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {!loading && jobs.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No jobs found.</p>}
      </div>

      {/* Bulk status dialog */}
      <Dialog open={bulkStatus.open} onOpenChange={(o) => !bulkStatus.busy && setBulkStatus((b) => ({ ...b, open: o }))}>
        <DialogContent data-testid="bulk-status-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Change Status — {selected.size} job(s)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Select value={bulkStatus.status} onValueChange={(v) => setBulkStatus((b) => ({ ...b, status: v }))}>
              <SelectTrigger data-testid="bulk-status-select" className="h-11"><SelectValue placeholder="New status" /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`bulk-status-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea data-testid="bulk-status-remarks" value={bulkStatus.remarks} onChange={(e) => setBulkStatus((b) => ({ ...b, remarks: e.target.value }))} placeholder="Remarks (optional, added to each job's history)" rows={2} />
          </div>
          <DialogFooter>
            <Button onClick={applyBulkStatus} disabled={bulkStatus.busy || !bulkStatus.status} data-testid="bulk-status-apply" className="w-full sm:w-auto">
              {bulkStatus.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply to Selected"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk edit dialog */}
      <Dialog open={bulkEdit.open} onOpenChange={(o) => !bulkEdit.busy && setBulkEdit((b) => ({ ...b, open: o }))}>
        <DialogContent data-testid="bulk-edit-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Edit {selected.size} job(s)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {isAdmin && (
              <div>
                <Label>Assign to staff</Label>
                <Select value={bulkEdit.assigned} onValueChange={(v) => setBulkEdit((b) => ({ ...b, assigned: v }))}>
                  <SelectTrigger data-testid="bulk-edit-assign-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep">Keep current assignment</SelectItem>
                    <SelectItem value="none" data-testid="bulk-edit-assign-none">Unassign</SelectItem>
                    {staffList.map((s) => <SelectItem key={s.user_id} value={s.user_id} data-testid={`bulk-edit-assign-${s.user_id}`}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Expected completion date</Label>
              <Input data-testid="bulk-edit-date" type="date" value={bulkEdit.date} onChange={(e) => setBulkEdit((b) => ({ ...b, date: e.target.value }))} className="h-11 mt-1" />
              <p className="text-xs text-muted-foreground mt-1">Leave blank to keep existing dates.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={applyBulkEdit} disabled={bulkEdit.busy} data-testid="bulk-edit-apply" className="w-full sm:w-auto">
              {bulkEdit.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDelete.open} onOpenChange={(o) => !bulkDelete.busy && setBulkDelete((b) => ({ ...b, open: o }))}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} job(s)?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes the selected jobs along with their notes and history. Photos will be removed from view. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyBulkDelete} disabled={bulkDelete.busy} data-testid="bulk-delete-confirm" className="bg-destructive text-destructive-foreground">
              {bulkDelete.busy ? "Deleting…" : "Delete Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
